import React, { createContext, useContext, useState, useCallback, ReactNode, useRef, useEffect } from 'react';
import { useUserContext } from '../../user/contexts/user.context';

export interface AnnotationLibraryItem {
  id: string;
  title: string;
  content: string;
}

interface AnnotationLibraryContextType {
  annotations: AnnotationLibraryItem[];
  addAnnotations: (annotations: AnnotationLibraryItem[]) => void;
  searchAnnotations: (query: string) => AnnotationLibraryItem[];
  clearLibrary: () => void;
}

const AnnotationLibraryContext = createContext<AnnotationLibraryContextType | undefined>(undefined);

interface AnnotationLibraryProviderProps {
  children: ReactNode;
}

export const AnnotationLibraryProvider: React.FC<AnnotationLibraryProviderProps> = ({ children }) => {
  const [annotations, setAnnotations] = useState<AnnotationLibraryItem[]>([]);
  const annotationsRef = useRef<AnnotationLibraryItem[]>([]);
  const { currentOrganization } = useUserContext();
  const prevOrgCodeRef = useRef<string | undefined>(undefined);

  // Garder la ref à jour
  useEffect(() => {
    annotationsRef.current = annotations;
  }, [annotations]);

  // Vider la bibliothèque quand l'organisation change
  useEffect(() => {
    // Attendre que l'organisation soit chargée
    if (!currentOrganization) {
      return;
    }

    const currentOrgCode = currentOrganization.code;
    
    // Si on a une organisation précédente et qu'elle est différente de l'actuelle
    if (prevOrgCodeRef.current && currentOrgCode && prevOrgCodeRef.current !== currentOrgCode) {
      // Changement d'organisation détecté → vider la bibliothèque
      setAnnotations([]);
    }
    
    // Mettre à jour la référence
    prevOrgCodeRef.current = currentOrgCode;
  }, [currentOrganization]);

  const addAnnotations = useCallback((newAnnotations: AnnotationLibraryItem[]) => {
    setAnnotations((prevAnnotations) => {
      const annotationMapByTitle = new Map<string, AnnotationLibraryItem>();
      
      // Ajouter les annotations existantes (en utilisant le titre comme clé)
      prevAnnotations.forEach((annotation) => {
        annotationMapByTitle.set(annotation.title.toLowerCase(), annotation);
      });
      
      // Ajouter ou mettre à jour avec les nouvelles annotations
      // Si le titre existe déjà, on garde la plus récente (la nouvelle)
      newAnnotations.forEach((annotation) => {
        annotationMapByTitle.set(annotation.title.toLowerCase(), annotation);
      });
      
      return Array.from(annotationMapByTitle.values());
    });
  }, []);

  const searchAnnotations = useCallback((query: string): AnnotationLibraryItem[] => {
    const currentAnnotations = annotationsRef.current;
    
    if (!query || query.length < 2) {
      return [];
    }
    
    // Recherche insensible à la casse dans le titre ET le contenu
    const lowerQuery = query.toLowerCase();
    const results = currentAnnotations
      .filter((annotation) => 
        annotation.title.toLowerCase().includes(lowerQuery) ||
        annotation.content.toLowerCase().includes(lowerQuery)
      )
      .slice(0, 5); // Limiter à 5 suggestions
    
    return results;
  }, []);

  const clearLibrary = useCallback(() => {
    setAnnotations([]);
  }, []);

  const value = {
    annotations,
    addAnnotations,
    searchAnnotations,
    clearLibrary,
  };

  return (
    <AnnotationLibraryContext.Provider value={value}>
      {children}
    </AnnotationLibraryContext.Provider>
  );
};

export const useAnnotationLibrary = (): AnnotationLibraryContextType => {
  const context = useContext(AnnotationLibraryContext);
  if (!context) {
    throw new Error('useAnnotationLibrary doit être utilisé à l\'intérieur d\'un AnnotationLibraryProvider');
  }
  return context;
};

